import { FC, MouseEvent, ReactElement, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore, PictureAsPdf } from "@mui/icons-material";

import {
  DutiesCard,
  ImportCard,
  AdvanceFilter,
  // DutiesUtilizedCard,
  UnifiedAnalyticsCard,
} from "./components";
import { Filters, initialFilters } from "./components/advance-filter";
import { DateRangeFilter, PlaceholderCard } from "@/components";
import { useAppSelector } from "@/store";
import { useEffectiveClientScope } from "@/hooks";
import {
  coins_dollar,
  distinct_hts,
  distinct_imports,
  duties_placeholder,
  filter_icon,
  imports_exports_placeholder,
  imports_placeholder,
  percent_circle,
} from "@/constants";
import {
  useGetDashboardDutySavingUtilizedQuery,
  useGetDashboardDutySpendQuery,
  useGetDashboardImportsExportsQuery,
  useGetDashboardImportsQuery,
  useGetDashboardManufacturersQuery,
  useGetDashboardMetricsQuery,
  useGetDashboardVisibilityFilterOptionsQuery,
  useGetDashboardTariffHtsQuery,
  useGetDashboardUpdateDateQuery,
  useGetDutyAnalyticsByAuthorityQuery,
  useGetCapeRefundSummaryQuery,
  useCreateAnalyticsReportMutation,
} from "@/services";
import useSnackbar from "@/hooks/useSnackbar.ts";
import { Range } from "react-date-range";
import { tokens } from "@/tokens";
import { format, differenceInMonths } from "date-fns";
import { roles } from "@/roles";
import {
  formatDateOrTime,
  formatNumberWithSuffix,
  numberWithCommas,
  getLastYearToDateRange,
  responsiveFontSizes,
  spacing,
} from "@/utils";

const TARIFF_CODE_PAGE_SIZE = 1000; // Fetch all codes at once

const Analytics: FC = (): ReactElement => {
  const { selectedClient, areClientsLoading } = useAppSelector(
    (state) => state.client,
  );
  const { areTenantsLoading } = useAppSelector(
    (state) => state.tenantManagement,
  );
  const { user } = useAppSelector((state) => state.auth);
  const { clientId, tenantId, isTDAdminTCD, shouldRequireClientSelection } =
    useEffectiveClientScope();

  const [dateFilter, setDateFilter] = useState<
    Pick<Range, "startDate" | "endDate">
  >(() => getLastYearToDateRange());

  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null,
  );
  const [filters, setFilters] = useState<Filters>({ ...initialFilters });
  const [draftFilters, setDraftFilters] = useState<Filters>({
    ...initialFilters,
  });

  const xAxisMode = useMemo((): "month" | "quarter" | "year" => {
    const start = dateFilter.startDate ?? new Date();
    const end = dateFilter.endDate ?? new Date();
    const months = differenceInMonths(end, start);
    if (months <= 18) return "month";
    if (months < 60) return "quarter"; // under 5 years
    return "year";
  }, [dateFilter]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [
      roles.SPOWNER,
      roles.SPPARTNER,
      roles.TDADMIN,
    ] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const appliedFilterLength = useMemo(() => {
    return Object.values(filters).filter(Boolean).length;
  }, [filters]);

  const openFilterModal = (event: MouseEvent<HTMLButtonElement>) => {
    setDraftFilters(filters);
    setAnchorElement(event.currentTarget);
  };

  const commonFilters = useMemo(() => {
    // If both dates are undefined, it's "All Time" - don't send date filters
    const isAllTime = !dateFilter.startDate && !dateFilter.endDate;

    return {
      // Only include date filters if not "All Time"
      ...(isAllTime
        ? {}
        : {
            dateFrom: format(dateFilter.startDate ?? new Date(), "yyyy-MM-dd"),
            dateTo: format(dateFilter.endDate ?? new Date(), "yyyy-MM-dd"),
          }),
      clientId,
      ...(isTDAdminTCD && tenantId && { tenantId }),
      ...(filters.filerCode && { filerCode: filters.filerCode }),
      ...(filters.importerNumber && { importerNumber: filters.importerNumber }),
    };
  }, [
    dateFilter,
    clientId,
    filters.filerCode,
    filters.importerNumber,
    isTDAdminTCD,
    tenantId,
  ]);

  const commonOptions = useMemo(
    () => ({
      skip: shouldRequireClientSelection,
      refetchOnMountOrArgChange: true,
    }),
    [shouldRequireClientSelection],
  );

  const { show: toast } = useSnackbar();
  const [createAnalyticsReport, { isLoading: isExportingPdf }] =
    useCreateAnalyticsReportMutation();

  // Generate the branded analytics PDF server-side for the current date range
  // and applied filters, then download the returned (base64) document.
  const handleExportPdf = async (): Promise<void> => {
    try {
      const { contentBase64, filename } = await createAnalyticsReport({
        dateFrom: commonFilters.dateFrom,
        dateTo: commonFilters.dateTo,
        clientId: commonFilters.clientId,
        ...(isTDAdminTCD && tenantId ? { tenantId } : {}),
        ...(filters.filerCode ? { filerCode: filters.filerCode } : {}),
        ...(filters.importerNumber
          ? { importerNumber: filters.importerNumber }
          : {}),
      }).unwrap();

      const binary = atob(contentBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "trade-analytics.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast("Analytics report downloaded", "", "success");
    } catch {
      toast("Could not generate the analytics report", "", "error");
    }
  };

  const filterOptionFilters = useMemo(() => {
    const isAllTime = !dateFilter.startDate && !dateFilter.endDate;

    return {
      ...(isAllTime
        ? {}
        : {
            dateFrom: format(dateFilter.startDate ?? new Date(), "yyyy-MM-dd"),
            dateTo: format(dateFilter.endDate ?? new Date(), "yyyy-MM-dd"),
          }),
      clientId,
      ...(isTDAdminTCD && tenantId && { tenantId }),
      ...(draftFilters.filerCode && { filerCode: draftFilters.filerCode }),
      ...(draftFilters.importerNumber && {
        importerNumber: draftFilters.importerNumber,
      }),
    };
  }, [clientId, dateFilter, draftFilters, isTDAdminTCD, tenantId]);

  const { data: lastUpdated, isFetching: isFetchingDate } =
    useGetDashboardUpdateDateQuery(
      {
        params: {
          clientId: commonFilters.clientId,
          ...(isTDAdminTCD && tenantId && { tenantId }),
        },
      },
      commonOptions,
    );

  const {
    data: importsData,
    isLoading: isImportsLoading,
    isFetching: isImportsFetching,
  } = useGetDashboardImportsQuery(
    {
      dashboardVisibility: {
        ...commonFilters,
        groupBy: xAxisMode === "year" ? "year" : "month",
      },
    },
    { ...commonOptions },
  );

  const { data: _dutySavingData, isFetching: isFetchingDutySaving } =
    useGetDashboardDutySavingUtilizedQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions },
    );

  const { data: manufacturersData, isFetching: isManufacturersFetching } =
    useGetDashboardManufacturersQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions },
    );

  const {
    data: tariffData,
    isLoading: isTariffLoading,
    isFetching: isTariffFetcing,
  } = useGetDashboardTariffHtsQuery(
    {
      dashboardVisibility: {
        ...commonFilters,
        minThreshold: 0,
        pageNo: 1,
        pageSize: TARIFF_CODE_PAGE_SIZE,
      },
    },
    { ...commonOptions },
  );

  const { data: dutySpendData, isFetching: isDutySpendFetching } =
    useGetDashboardDutySpendQuery(
      {
        dashboardVisibility: {
          dateFrom: commonFilters.dateFrom,
          dateTo: commonFilters.dateTo,
          clientId,
          ...(isTDAdminTCD && tenantId && { tenantId }),
          ...(commonFilters.filerCode && {
            filerCode: commonFilters.filerCode,
          }),
          ...(commonFilters.importerNumber && {
            importerNumber: commonFilters.importerNumber,
          }),
        },
      },
      { ...commonOptions },
    );

  const { data: metricsData, isFetching: isMetricsFetching } =
    useGetDashboardMetricsQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions },
    );

  const { data: filterOptionsData, isFetching: isFilterOptionsFetching } =
    useGetDashboardVisibilityFilterOptionsQuery(
      {
        dashboardVisibility: {
          ...filterOptionFilters,
        },
      },
      {
        ...commonOptions,
        skip: commonOptions.skip || !anchorElement,
      },
    );

  const { data: importExportData, isFetching: isImportExportFetching } =
    useGetDashboardImportsExportsQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions },
    );

  const {
    data: dutyAnalyticsData,
    isLoading: isDutyAnalyticsLoading,
    isFetching: isDutyAnalyticsFetching,
  } = useGetDutyAnalyticsByAuthorityQuery(
    {
      input: {
        dateFrom: commonFilters.dateFrom,
        dateTo: commonFilters.dateTo,
        clientId,
        tenantId: isTDAdminTCD ? tenantId : undefined,
        filerCode: commonFilters.filerCode,
        importerNumber: commonFilters.importerNumber,
      },
    },
    { ...commonOptions },
  );

  const { data: capeRefundData, isFetching: isCapeRefundFetching } =
    useGetCapeRefundSummaryQuery(
      {
        input: {
          ...commonFilters,
        },
      },
      { ...commonOptions },
    );

  const totalDutiesPaid = useMemo(() => {
    return dutySpendData?.data?.totalDutiesPaid ?? 0;
  }, [dutySpendData]);

  // Combined IEEPA CAPE refund (certified + pending) shown as its own bar in
  // the Duties card. Kept separate from duties paid/owed — it is money back,
  // not a duty.
  const capeRefund = useMemo(() => {
    const certified = capeRefundData?.data?.capeCertifiedAmount ?? 0;
    const pending = capeRefundData?.data?.capePendingAmount ?? 0;
    return { certified, pending, total: certified + pending };
  }, [capeRefundData]);

  const vendors: IVendor[] = useMemo(() => {
    if (!manufacturersData?.data) return [];

    return manufacturersData.data.map(
      ({
        _id = "",
        manufacturer = "",
        manufacturerName = "",
        customValue = 0,
        tariffDuty = 0,
        tariffValue = 0,
      }): IVendor => {
        return {
          _id,
          manufacturer,
          manufacturerName,
          customValue,
          tariffDuty,
          tariffValue,
        };
      },
    );
  }, [manufacturersData]);

  const {
    avgShipmentValue,
    distinctImports,
    entryLines,
    effectiveDutyRates,
    distinctHts,
    aceRange,
  } = useMemo(() => {
    const {
      avgShipmentValue = 0,
      distinctImports = 0,
      entryLines = 0,
      effectiveDutyRates = 0,
      distinctHts = 0,
      aceRange,
    } = metricsData?.data ?? {};

    return {
      avgShipmentValue: numberWithCommas(avgShipmentValue),
      distinctImports: formatNumberWithSuffix(distinctImports),
      entryLines: formatNumberWithSuffix(entryLines),
      effectiveDutyRates: Number(effectiveDutyRates).toFixed(2) + "%",
      distinctHts: formatNumberWithSuffix(distinctHts),
      aceRange,
    };
  }, [metricsData]);

  const originFooterData = useMemo(() => {
    const {
      customsValue = 0,
      customsDuty = 0,
      distinctImports = 0,
      effectiveDutyRates = 0,
    } = metricsData?.data ?? {};
    return {
      customsValue,
      customsDuty,
      distinctImports,
      effectiveDutyRate: Number(effectiveDutyRates),
    };
  }, [metricsData]);

  const isAnyApiLoading = useMemo(() => {
    return [
      isFetchingDate,
      isFetchingDutySaving,
      isManufacturersFetching,
      isTariffLoading,
      isImportsLoading,
      isDutySpendFetching,
      isMetricsFetching,
      isImportExportFetching,
    ].some(Boolean);
  }, [
    isFetchingDate,
    isFetchingDutySaving,
    isManufacturersFetching,
    isTariffLoading,
    isImportsLoading,
    isDutySpendFetching,
    isMetricsFetching,
    isImportExportFetching,
  ]);

  return (
    <Box
      sx={{
        mx: { xs: -1.25, md: -2.5 },
        mt: { xs: -0.75, md: -1.5 },
        px: { xs: 1.25, md: 2.5 },
        pt: { xs: 0.75, md: 1.5 },
        pb: 2,
        minHeight: "100%",
        bgcolor: tokens.bg.muted,
        borderRadius: `${tokens.radius.md} ${tokens.radius.md} 0 0`,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={spacing.md}
        gap={{ xs: 1.5, md: 0 }}
      >
        <Box sx={{ pl: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: responsiveFontSizes["lg"],
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
            }}
          >
            {selectedClient?.firstName || "Import Overview"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: responsiveFontSizes.sm,
              fontWeight: tokens.fontWeight.normal,
              color: tokens.text.secondary,
              mt: spacing.xs,
            }}
          >
            Import analytics and duty breakdown
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          gap={{ xs: 1, md: 2 }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            {aceRange?.minDate && aceRange?.maxDate && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: responsiveFontSizes.xs,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.body,
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                ACE Data: {format(new Date(aceRange.minDate), "MMM d, yyyy")} –{" "}
                {format(new Date(aceRange.maxDate), "MMM d, yyyy")}
              </Typography>
            )}
            {lastUpdated && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: "9px", md: "10px" },
                  fontWeight: tokens.fontWeight.normal,
                  color: tokens.text.muted,
                  whiteSpace: "nowrap",
                  display: "block",
                  mt: 0.25,
                }}
              >
                Last refreshed: {formatDateOrTime(lastUpdated, "both")}
              </Typography>
            )}
          </Box>
          <DateRangeFilter value={dateFilter} onSelectDate={setDateFilter} />
          <Button
            variant="white-outlined"
            onClick={handleExportPdf}
            disabled={
              isExportingPdf ||
              shouldRequireClientSelection ||
              !commonFilters.clientId
            }
            startIcon={
              isExportingPdf ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                <PictureAsPdf sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              minHeight: "auto",
              py: 0.75,
              px: 2,
              whiteSpace: "nowrap",
              flex: { xs: 1, sm: "initial" },
            }}
          >
            <Typography
              sx={{ fontSize: tokens.fontSize.md }}
              fontWeight={tokens.fontWeight.semibold}
            >
              {isExportingPdf ? "Generating…" : "Export PDF"}
            </Typography>
          </Button>
          <Button
            variant="primary-contained"
            onClick={openFilterModal}
            id="advance-filter-anchor"
            aria-controls={anchorElement ? "advance-filter-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={anchorElement ? "true" : undefined}
            sx={{
              minWidth: 230,
              minHeight: "auto",
              py: 0.75,
              px: 2,
              flex: {
                xs: 1,
                sm: "initial",
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <img src={filter_icon} alt="filter-icon" width={16} height={16} />
              <Typography
                sx={{ fontSize: tokens.fontSize.md }}
                color="white"
                fontWeight={tokens.fontWeight.semibold}
              >
                Advance Filter
              </Typography>
              {appliedFilterLength > 0 && (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    background: "#FFFFFF26",
                    display: "grid",
                    placeContent: "center",
                    borderRadius: "50%",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="white"
                    fontWeight={tokens.fontWeight.semibold}
                  >
                    {appliedFilterLength}
                  </Typography>
                </Box>
              )}
              <ExpandMore
                sx={{
                  color: "white",
                  fontSize: 18,
                }}
              />
            </Stack>
          </Button>
          <AdvanceFilter
            anchorEl={anchorElement}
            appliedFilters={filters}
            onFiltersChange={setFilters}
            onDraftFiltersChange={setDraftFilters}
            filterOptions={
              filterOptionsData?.data ?? { brokerOptions: [], iorOptions: [] }
            }
            isLoadingOptions={isFilterOptionsFetching}
            onClose={() => setAnchorElement(null)}
          />
        </Stack>
      </Stack>
      {isAnyApiLoading || areClientsLoading || areTenantsLoading ? (
        <Stack flex={1} justifyContent="center" alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={2.25} mb={2.5} data-testid="analytics-grid">
          <Grid item xs={12} md={8}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <ImportCard
                data={importsData?.data ?? []}
                xAxisMode={xAxisMode}
                isLoading={isImportsFetching}
                totalDuties={dutyAnalyticsData?.data?.grandTotal ?? 0}
                metrics={[
                  {
                    title: "Avg. Shipment",
                    value: "$" + avgShipmentValue,
                    icon: coins_dollar,
                  },
                  {
                    title: "Entries / Lines",
                    value: `${distinctImports} / ${entryLines}`,
                    icon: distinct_imports,
                  },
                  {
                    title: "Eff. Duty Rate",
                    value: effectiveDutyRates,
                    icon: percent_circle,
                  },
                  {
                    title: "Distinct HTS",
                    value: distinctHts,
                    icon: distinct_hts,
                  },
                ]}
              />
            ) : (
              <PlaceholderCard
                title="Overall Import Value"
                description="Track and analyze the total value of all imports over time"
                imgUrl={imports_placeholder}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <DutiesCard
                data={dutyAnalyticsData}
                isLoading={isDutyAnalyticsLoading}
                isFetching={isDutyAnalyticsFetching}
                totalDutiesPaid={totalDutiesPaid}
                capeRefundTotal={capeRefund.total}
                capeCertifiedAmount={capeRefund.certified}
                capePendingAmount={capeRefund.pending}
                isCapeRefundFetching={isCapeRefundFetching}
              />
            ) : (
              <PlaceholderCard
                title="Overall Duties Paid"
                description="Monitor total customs duties paid on imports, with detailed breakdowns by category and automatic duty calculations."
                imgUrl={duties_placeholder}
              />
            )}
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <DutiesUtilizedCard data={dutySavingData?.data ?? []} />
          </Grid> */}
          <Grid item xs={12}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <UnifiedAnalyticsCard
                importExportData={importExportData?.data || []}
                originFooterData={originFooterData}
                tariffData={tariffData?.data ?? []}
                tariffIsLoading={isTariffFetcing}
                vendorsData={vendors}
              />
            ) : (
              <PlaceholderCard
                title="Imports by Country of Origin"
                description="Analyze import values and duty rates by country of origin to identify cost-saving opportunities and optimize your supply chain."
                imgUrl={imports_exports_placeholder}
                centerContent
                cardStyles={{
                  minHeight: "660px",
                }}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Analytics;
